import { createContext, forwardRef, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '@/components/ui/daisy/button';

import {
  ToggleGroupContextValue,
  ToggleGroupItemProps,
  ToggleGroupProps,
} from './types';

const ToggleGroupContext = createContext<ToggleGroupContextValue>({
  value: '',
  multiple: false,
  onItemClick: () => {},
});

export const useToggleGroupContext = () => useContext(ToggleGroupContext);

const ToggleGroupBase = ({
  className,
  children,
  ...props
}: ToggleGroupProps) => {
  const handleMultipleChange = (itemValue: string) => {
    if (!props.multiple) return;
    const currentValues = Array.isArray(props.value) ? props.value : [];
    const isSelected = currentValues.includes(itemValue);

    if (isSelected) {
      props.onChange(currentValues.filter(v => v !== itemValue));
      return;
    }
    props.onChange([...currentValues, itemValue]);
  };

  const handleItemClick = (itemValue: string) => {
    if (props.multiple) handleMultipleChange(itemValue);
    else props.onChange(itemValue);
  };

  const classes = twMerge('flex flex-wrap gap-2', className);

  return (
    <ToggleGroupContext.Provider
      value={{
        value: props.value,
        multiple: props.multiple ?? false,
        onItemClick: handleItemClick,
      }}
    >
      <div role="group" className={classes}>
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
};

const Item = forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ value: itemValue, variant, children, ...buttonProps }, ref) => {
    const { value, multiple, onItemClick } = useToggleGroupContext();

    const isActive = multiple
      ? Array.isArray(value) && value.includes(itemValue)
      : value === itemValue;

    const isVariantByState = typeof variant === 'function';

    return (
      <Button
        ref={ref}
        active={!isVariantByState && isActive}
        variant={isVariantByState ? variant(isActive) : variant}
        aria-pressed={isActive}
        type="button"
        onClick={() => onItemClick(itemValue)}
        {...buttonProps}
      >
        {children}
      </Button>
    );
  },
);

ToggleGroupBase.displayName = 'ToggleGroup';
Item.displayName = 'ToggleGroup.Item';

export const ToggleGroup = Object.assign(ToggleGroupBase, {
  Item,
});
